/**
 * Rozpočet tovaru v editore zákazky (KOLO 230) — regály CRC vedľa nábytku.
 *
 * User: *„jedna zákazka, dva rozpočty… CP spolu"*, *„obchodníka nezaujíma,
 * kde ide hrana, iba cena"*. Tovar nemá materiály, hrany, rozmery ani
 * výrobu, preto nie je v tabuľke dielcov nábytku: zostava (regál R1) a pod
 * ňou položky z cenníka tovaru — kód CRC, povrch a farba (pri CO), Ks, cena.
 * Ceny počíta server (réžia aj práca tovaru 0, zisk varianty 0) a cena
 * aktívneho tovaru je v cene zákazky.
 */
import { useEffect, useRef, useState } from "react";
import { VETA } from "@/lib/prava";
import { LayoutGrid, Plus, Store, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteDielec, useUpdateDielec } from "@/api/hooks/useDielce";
import { useDeleteZostava } from "@/api/hooks/useZostavy";
import { useUpdateZakazka } from "@/api/hooks/useZakazky";
import { useTovarFarby, type TovarFarba } from "@/api/hooks/useTovar";
import { extractErrorMessage } from "@/api/client";
import type { Dielec, Varianta, ZakazkaDetail, Zostava } from "@/api/schemas";
import { formatEur } from "@/lib/format";
import { popisPovrchu, volitFarbu } from "@/lib/tovar";
import { PridatTovarDialog } from "@/components/tovar/PridatTovarDialog";
import { RegalDialog } from "@/components/tovar/RegalDialog";
import { NahladRegalu, type ZostavaNahladu } from "@/components/tovar/NahladRegalu";
import { cn } from "@/lib/utils";

/** Množstvo riadku — vlastná položka (montáž) má desatinné v svojej MJ. */
function mnozstvo(d: Dielec): number {
  if (d.vlastna_nakup != null) return Number(d.vlastna_mnozstvo ?? 1);
  return d.pocet_ks;
}

function mj(d: Dielec): string {
  if (d.vlastna_nakup != null) return d.vlastna_mj ?? "ks";
  return d.material_direct?.mj ?? "ks";
}

/** Cena za MJ a spolu za riadok (1 kus zostavy) — `cena_dielca` je za 1 ks,
 * pri vlastnej položke už za celé množstvo. */
function ceny(d: Dielec): { zaMj: number | null; spolu: number | null } {
  if (d.cena_dielca == null) return { zaMj: null, spolu: null };
  const c = Number(d.cena_dielca);
  if (d.vlastna_nakup != null) {
    const n = mnozstvo(d);
    return { zaMj: n > 0 ? c / n : c, spolu: c };
  }
  return { zaMj: c, spolu: c * d.pocet_ks };
}

function FarbaBunka({
  dielec,
  farby,
  onZmena,
}: {
  dielec: Dielec;
  farby: TovarFarba[];
  onZmena: (farba: string) => void;
}): JSX.Element {
  const m = dielec.material_direct;
  if (!volitFarbu(m)) {
    return <span className="text-muted-foreground">{popisPovrchu(m, null) ?? "—"}</span>;
  }
  const hodnota = dielec.farba ?? "";
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs text-muted-foreground">CO</span>
      <Select value={hodnota || undefined} onValueChange={onZmena}>
        <SelectTrigger
          className={cn("h-7 w-[150px] text-xs", !hodnota && "border-amber-500 text-amber-700")}
          aria-label={`Farba ${m?.nazov ?? ""}`}
        >
          <SelectValue placeholder="doplň farbu" />
        </SelectTrigger>
        <SelectContent>
          {farby.map((f) => (
            <SelectItem key={f.kod} value={f.kod} className="text-xs">
              <span className="font-mono">{f.kod}</span> {f.nazov}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PocetBunka({
  dielec,
  onUloz,
}: {
  dielec: Dielec;
  onUloz: (hodnota: number) => void;
}): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const zobraz = text ?? String(mnozstvo(dielec)).replace(".", ",");
  function uloz(): void {
    if (text === null) return;
    const n = Number(text.replace(",", "."));
    setText(null);
    if (!Number.isFinite(n) || n <= 0 || n === mnozstvo(dielec)) return;
    onUloz(n);
  }
  return (
    <Input
      value={zobraz}
      inputMode="decimal"
      className="h-7 w-16 text-right text-xs"
      aria-label={`Počet ${dielec.nazov_strojovy ?? ""}`}
      onChange={(e) => setText(e.target.value)}
      onBlur={uloz}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") setText(null);
      }}
    />
  );
}

/** Vlastná položka bez ceny (nákup 0 — z konfigurátora regálu: zarážky, LED,
 * diel mimo cenníka, KOLO 232) — obchodník doplní nákup priamo v riadku. */
const bezCeny = (d: Dielec): boolean => d.vlastna_nakup != null && Number(d.vlastna_nakup) === 0;

function NakupBunka({
  dielec,
  onUloz,
}: {
  dielec: Dielec;
  onUloz: (nakup: string) => void;
}): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  function uloz(): void {
    if (text === null) return;
    const n = Number(text.replace(",", ".").replace(/\s|€/g, ""));
    setText(null);
    if (!Number.isFinite(n) || n <= 0) return;
    onUloz(String(n));
  }
  return (
    <div className="flex items-center justify-end gap-1">
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
        doplň cenu
      </span>
      <Input
        value={text ?? ""}
        placeholder="nákup €"
        inputMode="decimal"
        className="h-7 w-20 border-amber-500 text-right text-xs"
        aria-label={`Nákupná cena ${dielec.nazov_strojovy ?? ""}`}
        onChange={(e) => setText(e.target.value)}
        onBlur={uloz}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setText(null);
        }}
      />
    </div>
  );
}

function ZostavaTovaru({
  zakazkaId,
  zostava,
  farby,
  onPridat,
  onVlastna,
  onRegal,
  vybranyKluc = null,
  onVyberRiadok,
  onVyberZostavu,
}: {
  zakazkaId: string;
  zostava: Zostava;
  farby: TovarFarba[];
  onPridat: (z: Zostava) => void;
  onVlastna: (zostavaId: string) => void;
  /** Upraviť regál — zostava je z konfigurátora (KOLO 232). */
  /** Bez neho (rozpočtár — tovar len na čítanie) zostava tlačidlo regálu nemá. */
  onRegal?: (uid: string) => void;
  /** Náhľad regálu (KOLO 237): vybraný riadok (`dielec.regal_kluc`) tejto zostavy. */
  vybranyKluc?: string | null;
  /** Klik na riadok z konfigurátora — rozsvieti jeho diely v náhľade. */
  onVyberRiadok?: (kluc: string) => void;
  /** Klik na hlavičku zostavy z konfigurátora — náhľad ju ukáže. */
  onVyberZostavu?: () => void;
}): JSX.Element {
  const { toast } = useToast();
  const update = useUpdateDielec(zakazkaId);
  const zmaz = useDeleteDielec(zakazkaId);
  const zmazZostavu = useDeleteZostava(zakazkaId);
  // Server posiela len aktívne riadky (zmazané nie)
  const riadky = (zostava.dielce ?? []) as Dielec[];

  function chyba(err: unknown): void {
    toast({ variant: "destructive", title: "Zmena sa neuložila", description: extractErrorMessage(err) });
  }

  return (
    <section
      className="rounded-md border"
      aria-label={`Zostava ${zostava.kod} ${zostava.nazov}`}
      data-regal-uid={zostava.regal_kluc ?? undefined}
    >
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2",
          onVyberZostavu ? "cursor-pointer" : "",
        )}
        onClick={(e) => {
          // tlačidlá v hlavičke majú vlastnú akciu
          if (onVyberZostavu && !(e.target as Element).closest("button, input, select")) onVyberZostavu();
        }}
      >
        <span className="rounded bg-sky-700 px-1.5 py-0.5 font-mono text-xs font-semibold text-white">
          {zostava.kod}
        </span>
        <span className="min-w-0 flex-1 truncate font-medium">
          {zostava.nazov}
          {zostava.popis ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{zostava.popis}</span>
          ) : null}
        </span>
        {zostava.regal_kluc ? (
          <span
            className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] text-sky-900"
            title="Zostava z konfigurátora regálu — Upraviť regál nahradí jej položky, ručne pridané ostanú"
          >
            z konfigurátora
          </span>
        ) : null}
        {zostava.pocet_ks > 1 ? (
          <span className="text-xs text-muted-foreground">{zostava.pocet_ks} ks</span>
        ) : null}
        <span className="text-sm font-semibold tabular-nums">{formatEur(zostava.cena_celkom)}</span>
        {zostava.regal_kluc && onRegal ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={() => onRegal(zostava.regal_kluc as string)}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Upraviť regál
          </Button>
        ) : null}
        <Button size="sm" variant="outline" className="h-7" onClick={() => onPridat(zostava)}>
          <Plus className="h-3.5 w-3.5" />
          Tovar
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7"
          title="Montáž, doprava, položka mimo cenníka"
          onClick={() => onVlastna(zostava.id)}
        >
          Vlastná položka
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7"
          aria-label={`Zmazať zostavu ${zostava.kod}`}
          title="Zmazať zostavu (Ctrl+Z vráti)"
          onClick={() => zmazZostavu.mutate(zostava.id, { onError: chyba })}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {riadky.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">
          Prázdna zostava — pridaj tovar z cenníka (+ Tovar).
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-1.5 font-medium">Kód</th>
                <th className="px-2 py-1.5 font-medium">Názov</th>
                <th className="px-2 py-1.5 font-medium">Povrch / farba</th>
                <th className="px-2 py-1.5 text-right font-medium">Ks</th>
                <th className="px-2 py-1.5 font-medium">MJ</th>
                <th className="px-2 py-1.5 text-right font-medium">Cena za MJ</th>
                <th className="px-2 py-1.5 text-right font-medium">Spolu</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {riadky.map((d) => {
                const c = ceny(d);
                const m = d.material_direct;
                const zRegalu = Boolean(d.regal_kluc && onVyberRiadok);
                const vybrany = zRegalu && d.regal_kluc === vybranyKluc;
                return (
                  <tr
                    key={d.id}
                    className={cn(
                      "border-t",
                      zRegalu ? "cursor-pointer hover:bg-muted/40" : "",
                      vybrany ? "bg-vyber-podklad hover:bg-vyber-podklad" : "",
                    )}
                    data-regal-kluc={d.regal_kluc ?? undefined}
                    aria-selected={zRegalu ? vybrany : undefined}
                    onClick={(e) => {
                      if (!zRegalu || (e.target as Element).closest("button, input, select, [role=combobox]")) return;
                      onVyberRiadok?.(d.regal_kluc as string);
                    }}
                  >
                    <td className="px-3 py-1.5 align-top">
                      {m?.tovar_kod ? (
                        <>
                          <div className="font-mono text-xs">{m.tovar_kod}</div>
                          {d.karta_abry ? (
                            <div className="font-mono text-[10px] text-muted-foreground" title="Karta ABRY">
                              ABRA {d.karta_abry}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">vlastná</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">{d.nazov_strojovy ?? m?.nazov ?? "—"}</td>
                    <td className="px-2 py-1.5 align-top">
                      <FarbaBunka
                        dielec={d}
                        farby={farby}
                        onZmena={(farba) =>
                          update.mutate({ id: d.id, data: { farba } }, { onError: chyba })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right align-top">
                      <PocetBunka
                        dielec={d}
                        onUloz={(n) =>
                          update.mutate(
                            {
                              id: d.id,
                              data:
                                d.vlastna_nakup != null
                                  ? { vlastna_mnozstvo: String(n) }
                                  : { pocet_ks: Math.round(n) },
                            },
                            { onError: chyba },
                          )
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top text-xs text-muted-foreground">{mj(d)}</td>
                    <td className="px-2 py-1.5 text-right align-top tabular-nums">
                      {bezCeny(d) ? (
                        <NakupBunka
                          dielec={d}
                          onUloz={(nakup) =>
                            update.mutate({ id: d.id, data: { vlastna_nakup: nakup } }, { onError: chyba })
                          }
                        />
                      ) : (
                        formatEur(c.zaMj)
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right align-top font-medium tabular-nums">
                      {formatEur(c.spolu)}
                    </td>
                    <td className="px-1 py-1 align-top">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        aria-label={`Zmazať ${d.nazov_strojovy ?? m?.nazov ?? "riadok"}`}
                        onClick={() => zmaz.mutate(d.id, { onError: chyba })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function TovarVarianta({
  zakazka,
  varianta,
  onNovaZostava,
  onVlastna,
  lenCitanie = false,
}: {
  zakazka: ZakazkaDetail;
  varianta: Varianta;
  /** Otvorí okno novej zostavy (to isté ako + Zostava v paneli nástrojov). */
  onNovaZostava: () => void;
  /** Okno vlastnej položky editora (montáž, doprava). */
  onVlastna: (zostavaId: string) => void;
  /**
   * Tovar upravuje obchodník (KOLO 231) — rozpočtár ho vidí len na
   * čítanie: farba, Ks, + Tovar, Vlastná položka, zmazanie aj Dať do ceny
   * zákazky sú vypnuté.
   */
  lenCitanie?: boolean;
}): JSX.Element {
  const { toast } = useToast();
  const farby = useTovarFarby().data ?? [];
  const updateZakazka = useUpdateZakazka();
  const [pridatDo, setPridatDo] = useState<Zostava | null>(null);
  // Konfigurátor regálu (KOLO 232): null = zatvorený, "" = celý regál, inak uid zostavy
  const [regal, setRegal] = useState<string | null>(null);
  const zostavy = (varianta.zostavy ?? []) as Zostava[];
  const maRegal = Boolean(varianta.ma_regal);
  const jeAktivny = zakazka.aktivna_tovar_varianta_id === varianta.id;
  // Náhľad regálu (KOLO 237): zostavy z konfigurátora, vybraná zostava a riadok
  const regalZostavy: ZostavaNahladu[] = maRegal
    ? zostavy
        .filter((z) => z.regal_kluc)
        .map((z) => ({ uid: z.regal_kluc as string, kod: z.kod, nazov: z.nazov, pocet: z.pocet_ks }))
    : [];
  const [vyber, setVyber] = useState<{ uid: string | null; kluc: string | null; posun?: number }>({
    uid: null,
    kluc: null,
  });
  const uidNahladu =
    regalZostavy.find((z) => z.uid === vyber.uid)?.uid ?? regalZostavy[0]?.uid ?? null;
  const koren = useRef<HTMLDivElement>(null);
  // klik na diel v obrázku → riadok do pohľadu
  useEffect(() => {
    if (!vyber.posun || !vyber.kluc || !uidNahladu) return;
    const sekcia = Array.from(koren.current?.querySelectorAll("section[data-regal-uid]") ?? []).find(
      (el) => el.getAttribute("data-regal-uid") === uidNahladu,
    );
    const riadok = Array.from(sekcia?.querySelectorAll("tr[data-regal-kluc]") ?? []).find(
      (el) => el.getAttribute("data-regal-kluc") === vyber.kluc,
    );
    riadok?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [vyber, uidNahladu]);

  return (
    <div ref={koren} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto" data-testid="tovar-varianta">
      {lenCitanie && maRegal ? (
        // mimo fieldsetu — vypnutý fieldset by vypol aj toto tlačidlo
        <div className="flex justify-end">
          <Button size="sm" variant="outline" className="h-7" onClick={() => setRegal("")}>
            <LayoutGrid className="h-3.5 w-3.5" />
            Pozrieť regál
          </Button>
        </div>
      ) : null}
      <fieldset disabled={lenCitanie} className="contents">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border bg-sky-50/60 px-3 py-2 dark:bg-sky-950/20">
        <span className="flex items-center gap-1.5 font-medium">
          <Store className="h-4 w-4 text-sky-700" />
          {varianta.nazov}
        </span>
        <span className="text-sm text-muted-foreground">
          Tovar s DPH{" "}
          <b className="text-foreground tabular-nums">{formatEur(varianta.cena_s_dph)}</b>
        </span>
        {lenCitanie ? (
          <span className="text-xs text-muted-foreground" title={VETA.tovar}>
            len na čítanie
          </span>
        ) : null}
        {jeAktivny ? (
          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-900">v cene zákazky</span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            disabled={updateZakazka.isPending}
            onClick={() =>
              updateZakazka.mutate(
                { id: zakazka.id, data: { aktivna_tovar_varianta_id: varianta.id } },
                {
                  onError: (err) =>
                    toast({
                      variant: "destructive",
                      title: "Tovar sa nepodarilo dať do ceny",
                      description: extractErrorMessage(err),
                    }),
                },
              )
            }
          >
            Dať do ceny zákazky
          </Button>
        )}
        {lenCitanie ? null : (
          <Button
            size="sm"
            variant={maRegal ? "outline" : "default"}
            className="h-7"
            onClick={() => setRegal("")}
            title="Konfigurátor regálu CRC — zostavy regálu do tovaru, diely Tandemu rozpočtárovi"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            {maRegal ? "Upraviť regál" : "Regál CRC"}
          </Button>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-x-4 text-sm text-muted-foreground">
          <span>
            Nábytok <b className="tabular-nums text-foreground">{formatEur(zakazka.cena_nabytku_s_dph)}</b>
          </span>
          <span>
            Tovar <b className="tabular-nums text-foreground">{formatEur(zakazka.cena_tovaru_s_dph)}</b>
          </span>
          <span>
            Zákazka spolu{" "}
            <b className="tabular-nums text-foreground">{formatEur(zakazka.cena_celkom_s_dph)}</b>
          </span>
        </span>
      </div>
      </fieldset>

      <div
        className={cn(
          uidNahladu
            ? "grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(360px,40%)] lg:items-start"
            : "flex flex-col gap-3",
        )}
      >
      <fieldset disabled={lenCitanie} className="flex min-w-0 flex-col gap-3">
      {zostavy.length === 0 ? (
        <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          <p>Rozpočet tovaru je prázdny.</p>
          {lenCitanie ? null : (
            <>
              <p className="mt-1">Pridaj zostavu (napr. Regál R1) a do nej tovar z cenníka CRC.</p>
              <Button size="sm" className="mt-3" onClick={onNovaZostava}>
                <Plus className="h-4 w-4" />
                Zostava
              </Button>
            </>
          )}
        </div>
      ) : (
        zostavy.map((z) => (
          <ZostavaTovaru
            key={z.id}
            zakazkaId={zakazka.id}
            zostava={z}
            farby={farby}
            onPridat={setPridatDo}
            onVlastna={onVlastna}
            onRegal={lenCitanie ? undefined : setRegal}
            vybranyKluc={z.regal_kluc && z.regal_kluc === uidNahladu ? vyber.kluc : null}
            onVyberRiadok={
              z.regal_kluc
                ? (kluc) => setVyber({ uid: z.regal_kluc as string, kluc })
                : undefined
            }
            onVyberZostavu={
              z.regal_kluc ? () => setVyber({ uid: z.regal_kluc as string, kluc: null }) : undefined
            }
          />
        ))
      )}
      </fieldset>
      {uidNahladu ? (
        // náhľad je mimo fieldsetu — rozpočtár (len na čítanie) prepína pohľady tiež
        <div className="order-first lg:order-none">
          <NahladRegalu
            variantaId={varianta.id}
            zostavy={regalZostavy}
            uid={uidNahladu}
            kluc={vyber.kluc}
            kluceRiadkov={(zostavy.find((z) => z.regal_kluc === uidNahladu)?.dielce ?? [])
              .map((d) => (d as Dielec).regal_kluc)
              .filter((k): k is string => Boolean(k))}
            onZostava={(uid) => setVyber({ uid, kluc: null })}
            onKluc={(kluc) => setVyber({ uid: uidNahladu, kluc, posun: Date.now() })}
          />
        </div>
      ) : null}
      </div>
      {regal !== null ? (
        <RegalDialog
          zakazkaId={zakazka.id}
          variantaId={varianta.id}
          uid={regal || null}
          lenCitanie={lenCitanie}
          onOpenChange={(o) => {
            if (!o) setRegal(null);
          }}
        />
      ) : null}
      <PridatTovarDialog
        zakazkaId={zakazka.id}
        zostava={pridatDo}
        farby={farby}
        onOpenChange={(o) => {
          if (!o) setPridatDo(null);
        }}
      />
    </div>
  );
}
